/**
 * Selectors related to user edits on data preparation revisions.
 */

import { areConnectionsEqual } from "@/data-preparation-tool/utils/edge-utils";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { assert, GUID } from "@faro-lotv/foundation";
import { isValid } from "@faro-lotv/ielement-types";
import {
  CaptureTreeEntityType,
  CreateOrUpdateClusterEntityParams,
  CreateOrUpdateRootEntityRequestBody,
  CreateOrUpdateScanEntityParams,
  isDeletedRevisionStatus,
  RegistrationEdgeRevision,
  RegistrationEdgeType,
  RegistrationStatus,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { selectIsSavingDraftChanges } from "../data-preparation-ui/draft-saving-selectors";
import {
  selectEdgesByType,
  selectEdgesMap,
} from "../revision/revision-edge-selectors";
import {
  selectRevisionEntityMap,
  selectRevisionTransformCache,
  UpdatedRevisionEntities,
} from "../revision/revision-entity-selectors";
import { EdgesMap, EntityMap } from "../revision/revision-slice";
import {
  EntityTransformOverrides,
  RevisionTransformCache,
} from "../revision/revision-transform-cache";
import { isEdgeTransformValid } from "../selector-helpers";
import {
  RegistrationEditMode,
  UserEditTransformScans,
} from "../user-edits/user-edit-types";
import {
  createEdgeEditIfNecessary,
  EDGES_DELETED_BY_DELETED_CONNECTION,
  EDGES_DELETED_BY_SCAN_TRANSFORM,
  isEdgeInvalidatedByTransformEdit,
  UpdatedRevisionEdges,
} from "./edge-editing-utils";
import {
  ConnectionEdit,
  isConnectionEdit,
  UserEdit,
  UserEditType,
} from "./user-edit-types";

/**
 * @param state The current application state
 * @returns Whether the user can edit the registration results.
 */
export function selectIsAnyEditModeEnabled(state: RootState): boolean {
  return selectEditMode(state) !== undefined;
}

/**
 * @param state The current application state
 * @returns All edits performed by the user
 */
export function selectUserEdits(state: RootState): UserEdit[] {
  return state.dataPreparationEditingState.userEdits;
}

/**
 * @param state The current application state
 * @returns Whether the user is currently in progress of transforming (moving) scans.
 */
export const selectIsTransformingScans = createSelector(
  [selectInProgressTransformEdit],
  (inProgressEdit): boolean => !!inProgressEdit,
);

/**
 * @param state The current application state.
 * @returns A map from entity ID to the transform overrides from user edits (moveScans).
 *  This includes only the most recent position or rotation for each entity from committed edits.
 */
export const selectEntityTransformOverridesFromUserEdits = createSelector(
  [selectUserEdits],
  (userEdits): EntityTransformOverrides => {
    const overrides: EntityTransformOverrides = {};

    // Process edits in order, so later edits override earlier ones
    for (const edit of userEdits) {
      if (edit.type === UserEditType.moveScans) {
        Object.assign(overrides, edit.newPoses);
      }
    }
    return overrides;
  },
);

/**
 * @param state The current application state.
 * @returns The transform edit the user is currently performing (moving scans), if any.
 */
export function selectInProgressTransformEdit(
  state: RootState,
): UserEditTransformScans | undefined {
  return state.dataPreparationEditingState.inProgressTransformEdit;
}

/**
 * @param state The current application state.
 * @returns The cached world transform considering all transform edits performed by the user.
 */
const selectUserEditTransformCache = createSelector(
  [selectUserEdits, selectInProgressTransformEdit],
  (userEdits, inProgressEdit): RevisionTransformCache => {
    const transformCache: RevisionTransformCache = {};

    for (const edit of userEdits) {
      if (edit.type !== UserEditType.moveScans) continue;

      for (const [entityId, transform] of Object.entries(
        edit.cachedWorldTransforms,
      )) {
        transformCache[entityId] = transform;
      }
    }

    if (inProgressEdit) {
      for (const [entityId, transform] of Object.entries(
        inProgressEdit.cachedWorldTransforms,
      )) {
        transformCache[entityId] = transform;
      }
    }

    return transformCache;
  },
);

/** @returns the entities with updated transforms */
export const selectUpdatedRevisionEntities = createSelector(
  [selectRevisionEntityMap, selectEntityTransformOverridesFromUserEdits],
  (entities, transformOverrides): UpdatedRevisionEntities => {
    let updatedRoot: CreateOrUpdateRootEntityRequestBody | undefined;
    const updatedClusters: CreateOrUpdateClusterEntityParams[] = [];
    const updatedScans: CreateOrUpdateScanEntityParams[] = [];

    for (const [id, transform] of Object.entries(transformOverrides)) {
      if (!transform) continue;

      const entity = entities[id];
      assert(entity, `Entity ${id} has an override but is not in the store`);

      const updateData = {
        pose: transform,
        // Moving an entity resets its registration status, since its previous
        // registration no longer reflects the new pose. Manually locked entities
        // are the exception: keep them locked so a move doesn't silently unlock them.
        registrationStatus:
          entity.registrationStatus === RegistrationStatus.lockedManually
            ? RegistrationStatus.lockedManually
            : RegistrationStatus.unknown,
      };

      switch (entity.type) {
        case CaptureTreeEntityType.root:
          updatedRoot = {
            ...entity,
            ...updateData,
          };
          break;
        case CaptureTreeEntityType.cluster:
          updatedClusters.push({
            ...entity,
            ...updateData,
          });
          break;
        default:
          updatedScans.push({
            ...entity,
            ...updateData,
          });
      }
    }

    return { updatedRoot, updatedClusters, updatedScans };
  },
);

/**
 * @param state The current application state
 * @returns All edits performed by the user in reversed order.
 *  Useful to show the most recent edits first.
 */
export const selectUserEditsReversed = createSelector(
  [selectUserEdits],
  (userEdits) => [...userEdits].reverse(),
);

/**
 * @param state The current application state
 * @returns All connection edits (add/delete) performed by the user.
 *  For each connection, only the most recent edit is included.
 */
export const selectMostRecentConnectionEdits = createSelector(
  [selectUserEditsReversed],
  (userEditsReversed) => {
    const connectionEdits: ConnectionEdit[] = [];

    for (const edit of userEditsReversed) {
      // Use the isConnectionEdit type guard to filter valid connections
      if (
        isConnectionEdit(edit) &&
        !connectionEdits.some(
          (c) => isConnectionEdit(c) && areConnectionsEqual(c, edit),
        )
      ) {
        connectionEdits.push(edit);
      }
    }

    return connectionEdits.reverse();
  },
);

/**
 * @param state The current application state
 * @returns All connections that the user added manually, in the order they were added.
 */
export const selectConnectionsAddedByUser = createSelector(
  [selectMostRecentConnectionEdits],
  (connectionEdits) =>
    connectionEdits.filter((edit) => edit.type === UserEditType.addConnection),
);

/**
 * @param state The current application state
 * @returns All connections that the user deleted manually, in the order they were deleted.
 */
export const selectConnectionsDeletedByUser = createSelector(
  [selectMostRecentConnectionEdits],
  (connectionEdits) =>
    connectionEdits.filter(
      (edit) => edit.type === UserEditType.deleteConnection,
    ),
);

/** @returns the final transform cache for the data preparation page, considering all user edits. */
export const selectDataPreparationTransformCache = createSelector(
  [selectRevisionTransformCache, selectUserEditTransformCache],
  (revisionCache, userEditCache): RevisionTransformCache => ({
    ...revisionCache,
    ...userEditCache,
  }),
);

/**
 * @param state The current application state.
 * @returns `true` if the user applied any unsaved edits to the revision.
 */
export const selectIsRevisionUserModified = createSelector(
  [selectUserEdits],
  (userEdits) => userEdits.length > 0,
);

/** @returns the edge updates to persist to the API after user edits. */
export const selectUpdatedRevisionEdges = createSelector(
  [
    selectEdgesMap,
    selectMostRecentConnectionEdits,
    selectUserEdits,
    selectRevisionEntityMap,
  ],
  (
    edgesMap: EdgesMap,
    connectionEdits: ConnectionEdit[],
    userEdits: UserEdit[],
    entityMap: EntityMap,
  ): UpdatedRevisionEdges => {
    const edges = Object.values(edgesMap).filter(isValid);
    const validEdges = edges.filter(
      (edge) => !isDeletedRevisionStatus(edge.status),
    );

    const newEdges: UpdatedRevisionEdges["newEdges"] = [];
    const deletedEdges = new Set<GUID>();

    // Process connection edits
    for (const edit of connectionEdits) {
      if (edit.type === UserEditType.addConnection) {
        // Delete any anti candidate edges for this connection
        for (const edge of validEdges) {
          if (
            edge.type === RegistrationEdgeType.antiCandidate &&
            areConnectionsEqual(edge, edit)
          ) {
            deletedEdges.add(edge.id);
          }
        }

        const newEdge = createEdgeEditIfNecessary(
          edges,
          RegistrationEdgeType.candidate,
          edit,
        );

        if (newEdge) {
          newEdges.push(newEdge);
        }
      } else {
        // Delete any candidate or global edges for this connection
        for (const edge of validEdges) {
          if (
            EDGES_DELETED_BY_DELETED_CONNECTION.includes(edge.type) &&
            areConnectionsEqual(edge, edit)
          ) {
            deletedEdges.add(edge.id);
          }
        }

        const newEdge = createEdgeEditIfNecessary(
          edges,
          RegistrationEdgeType.antiCandidate,
          edit,
        );

        if (newEdge) {
          newEdges.push(newEdge);
        }
      }
    }

    // Delete existing edges invalidated by transform edits
    for (const edge of validEdges) {
      if (!EDGES_DELETED_BY_SCAN_TRANSFORM.includes(edge.type)) {
        // Not all edges are affected by the user moving scans
        // They either don't have a transform or are not created based on the current scan's transform
        continue;
      }

      for (const edit of userEdits) {
        if (
          edit.type === UserEditType.moveScans &&
          isEdgeInvalidatedByTransformEdit(edge, edit, entityMap)
        ) {
          // Delete when source and target have been edited differently
          // In that case, the registration does not apply anymore
          deletedEdges.add(edge.id);
        }
      }
    }

    return {
      newEdges,
      deletedEdges,
    };
  },
);

/**
 * @param state The current application state
 * @returns The edit mode which is currently enabled, or `undefined` if editing is disabled.
 */
export function selectEditMode(
  state: RootState,
): RegistrationEditMode | undefined {
  return state.dataPreparationEditingState.editMode;
}

/**
 * @param state The current application state
 * @param mode The edit mode to check
 * @returns Whether the given edit mode is enabled and the user can currently interact with it.
 *  The interaction is suspended while draft changes are saved, so the persisted state cannot change mid-save.
 */
function isEditModeInteractive(
  state: RootState,
  mode: RegistrationEditMode,
): boolean {
  return selectEditMode(state) === mode && !selectIsSavingDraftChanges(state);
}

/**
 * @param state The current application state
 * @returns Whether the user can edit scans, i.e. move and rotate them.
 */
export function selectIsEditingScans(state: RootState): boolean {
  return isEditModeInteractive(state, RegistrationEditMode.editScans);
}

/**
 * @param state The current application state
 * @returns Whether the user can add new connections to the registration.
 */
export function selectIsAddingConnections(state: RootState): boolean {
  return isEditModeInteractive(state, RegistrationEditMode.addConnection);
}

/**
 * @param state The current application state
 * @returns Whether the user can delete existing registration edges.
 */
export function selectIsDeletingConnections(state: RootState): boolean {
  return isEditModeInteractive(state, RegistrationEditMode.deleteConnection);
}

/**
 * @param state The current application state
 * @returns All undid edits
 */
export function selectUndidUserEdits(state: RootState): UserEdit[] {
  return state.dataPreparationEditingState.undidUserEdits;
}

/**
 * @returns whether an entity has been moved (changed transform) via user edits
 * @param state The current application state
 * @param entityId the entity id to check
 */
export const selectHasEntityBeenTransformed = curryAppSelector(
  createSelector(
    [selectUserEdits, (state, entityId: GUID) => entityId],
    (edits: UserEdit[], entityId: GUID): boolean =>
      edits.some(
        (edit) =>
          edit.type === UserEditType.moveScans &&
          edit.scanIds.includes(entityId),
      ),
  ),
);

/**
 * @param state The current application state
 * @returns Whether there are unsaved edits
 */
export function selectHasUnsavedEdits(state: RootState): boolean {
  return state.dataPreparationEditingState.userEdits.length > 0;
}

/**
 * @param state The current application state
 * @returns Whether the user can undo the last edit (is there any action to undo?)
 */
export function selectCanUndo(state: RootState): boolean {
  return state.dataPreparationEditingState.userEdits.length > 0;
}

/**
 * @param state The current application state
 * @returns Whether the user can redo the last undone edit (is there any action to redo?)
 */
export function selectCanRedo(state: RootState): boolean {
  return state.dataPreparationEditingState.undidUserEdits.length > 0;
}

/**
 *
 * @param state The current application state
 * @returns The latest UserEdit that could be undone
 */
export function selectLatestUndoStoredEdit(
  state: RootState,
): UserEdit | undefined {
  const undidEdits = state.dataPreparationEditingState.userEdits;
  return undidEdits[undidEdits.length - 1];
}

/**
 *
 * @param state The current application state
 * @returns The latest UserEdit that could be redone
 */
export function selectLatestRedoStoredEdit(
  state: RootState,
): UserEdit | undefined {
  const redoneEdits = state.dataPreparationEditingState.undidUserEdits;
  return redoneEdits[redoneEdits.length - 1];
}

/**
 * @param state The current application state.
 * @param edge The registration edge to check for deletion.
 * @returns Whether the user has deleted the given edge.
 */
const selectIsEdgeDeletedByUser = curryAppSelector(
  createSelector(
    [
      selectConnectionsDeletedByUser,
      (state: RootState, edge: RegistrationEdgeRevision) => edge,
    ],
    (deletedConnections, edge) =>
      deletedConnections.some((deleted) => areConnectionsEqual(edge, deleted)),
  ),
);

/**
 * @param state The current application state.
 * @param edge The registration edge to check for validity.
 * @returns Whether the registration edge is valid, i.e. no related scans have been modified since the registration
 *  and the edge is not deleted by the user.
 */
export const selectIsRegistrationEdgeValid = curryAppSelector(
  createSelector(
    [
      selectDataPreparationTransformCache,
      (state, edge: RegistrationEdgeRevision) =>
        selectIsEdgeDeletedByUser(edge)(state),
      selectEdgesByType(RegistrationEdgeType.global),
      (state: RootState, edge: RegistrationEdgeRevision) => edge,
    ],
    (transformCache, isDeleted, globalEdges, edge) => {
      if (isDeleted) {
        return false;
      }

      // For global edges, ensure that the transform saved in the edge still matches the relative transform between the scans
      if (edge.type === RegistrationEdgeType.global) {
        return isEdgeTransformValid(transformCache, edge);
      }

      // For local edges, compare the transform of the corresponding global edge instead
      // The scans will have been moved due to the graph optimization, so the local edge transform is no longer valid
      if (edge.type === RegistrationEdgeType.local) {
        const globalEdge = globalEdges.find((global) =>
          areConnectionsEqual(global, edge),
        );

        if (!globalEdge) {
          // Usually, all local edges should have a corresponding global edge
          // But if not, keep the edge to still be able to show it
          return true;
        }

        // Ensure that the transform saved in the edge still matches the relative transform between the scans
        return isEdgeTransformValid(transformCache, globalEdge);
      }

      // Other edge types cannot be invalidated by moving scans
      return true;
    },
  ),
);
